import React, { useEffect, useState } from "react";
import axios from "axios";
import { Person } from "../Admin/ID/types";
import "../../Pages/Admin/Event/AdminEvent.css";

interface Country {
    code: string;
}

type PersonFormData = Person & {
    uploadImageBase64?: string;
};

interface Props {
    initialPerson?: Person | null;
    onSave: (person: PersonFormData) => Promise<void> | void;
    onCancel: () => void;
    currentUserId: number;
}

const PersonSimpleModalForm: React.FC<Props> = ({
    initialPerson,
    onSave,
    onCancel,
    currentUserId,
}) => {
    const isEdit = !!initialPerson?.personId;

    const [countries, setCountries] = useState<Country[]>([]);
    const [busy, setBusy] = useState(false);

    const [formData, setFormData] = useState<PersonFormData>(() => ({
        personId: initialPerson?.personId ?? 0,
        userId: initialPerson?.userId ?? currentUserId,

        firstName: initialPerson?.firstName ?? "",
        lastName: initialPerson?.lastName ?? "",
        dateOfBirth: initialPerson?.dateOfBirth,
        country: initialPerson?.country ?? "",

        personImagePath: initialPerson?.personImagePath ?? "",
        profileUrl: initialPerson?.profileUrl ?? "",
        created: initialPerson?.created ?? new Date().toISOString(),
        updated: initialPerson?.updated,

        // 🔥 ADD THESE
        events: initialPerson?.events ?? [],
        functions: initialPerson?.functions ?? [],

        uploadImageBase64: undefined,
        hasProfileImage: initialPerson?.hasProfileImage ?? false,
    }));

    const [previewImage, setPreviewImage] = useState<string | undefined>(
        initialPerson?.personImagePath ||
        initialPerson?.profileUrl ||
        undefined
    );

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/countries`
                );
                setCountries(res.data);
            } catch (err) {
                console.error("Failed to load countries", err);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        setFormData({
            personId: initialPerson?.personId ?? 0,
            userId: initialPerson?.userId ?? currentUserId,

            firstName: initialPerson?.firstName ?? "",
            lastName: initialPerson?.lastName ?? "",
            dateOfBirth: initialPerson?.dateOfBirth,
            country: initialPerson?.country ?? "",

            personImagePath: initialPerson?.personImagePath ?? "",
            profileUrl: initialPerson?.profileUrl ?? "",
            created: initialPerson?.created ?? new Date().toISOString(),
            updated: initialPerson?.updated,

            events: initialPerson?.events ?? [],
            functions: initialPerson?.functions ?? [],

            uploadImageBase64: undefined,
            hasProfileImage: initialPerson?.hasProfileImage ?? false,
        });

        setPreviewImage(
            initialPerson?.personImagePath ||
            initialPerson?.profileUrl ||
            undefined
        );
    }, [initialPerson, currentUserId]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onCancel]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const compressImage = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const img = new Image();

                img.onload = () => {
                    const maxWidth = 600;
                    const maxHeight = 600;
                    const quality = 0.7;

                    let { width, height } = img;

                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Could not create canvas context"));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    resolve(canvas.toDataURL("image/jpeg", quality));
                };

                img.onerror = () => reject(new Error("Image load failed"));
                img.src = reader.result as string;
            };

            reader.onerror = () => reject(new Error("File read failed"));
            reader.readAsDataURL(file);
        });
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setBusy(true);

            const compressed = await compressImage(file);

            setPreviewImage(compressed);
            setFormData(prev => ({
                ...prev,
                uploadImageBase64: compressed,
            }));
        } catch (err) {
            console.error(err);
            alert("Could not process image");
        } finally {
            setBusy(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            alert("First name and last name are required");
            return;
        }

        try {
            setBusy(true);

            await onSave(formData);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="eventform-card">
                    <div className="eventform-header">
                        <h2 className="eventform-title">
                            {isEdit ? "Edit Person" : "Create Person"}
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="eventform-grid">

                            <div className="field">
                                <label>First name</label>
                                <input
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="field">
                                <label>Last name</label>
                                <input
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="field">
                                <label>Date of birth</label>
                                <input
                                    type="date"
                                    name="dateOfBirth"
                                    value={
                                        formData.dateOfBirth
                                            ? formData.dateOfBirth.split("T")[0]
                                            : ""
                                    }
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="field">
                                <label>Country</label>
                                <select
                                    name="country"
                                    value={formData.country || ""}
                                    onChange={handleChange}
                                >
                                    <option value="">Select country</option>
                                    {countries.map(c => (
                                        <option key={c.code} value={c.code}>
                                            {c.code}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field" style={{ gridColumn: "1 / -1" }}>
                                <label>Profile image</label>

                                {previewImage && (
                                    <img
                                        src={previewImage}
                                        alt="preview"
                                        style={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            marginBottom: 10,
                                        }}
                                    />
                                )}

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>

                        </div>

                        <div className="eventform-actions">
                            <button type="button" onClick={onCancel} disabled={busy}>
                                Cancel
                            </button>

                            <button type="submit" disabled={busy}>
                                {busy ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PersonSimpleModalForm;